/**
 * The lifecycle pill a contest wears in headers, table rows and anywhere it is
 * referenced (design-system.md §6 "Status pill").
 *
 * Palette and geometry both come from `theme.tokens`, so no state can drift from
 * the token layer the gallery renders; the labels come from
 * `contests/lifecycle.ts`, so none can drift from the filter chips either.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { LIFECYCLE_LABELS } from '../../contests/lifecycle';
import type { StatusPillSize, StatusPillState } from '../../theme';

/**
 * The lifecycle labels come from the mapping layer, so the pill and the Contests
 * list's filter chips cannot name the same state two ways — `scheduled` reads
 * **Upcoming** in both.
 *
 * `finished` and `feedDisconnected` are the pill's own: the first is a *fixture*
 * status and the second is the *feed's*, and neither has a place in the contest
 * lifecycle.
 */
const LABELS: Record<StatusPillState, string> = {
  ...LIFECYCLE_LABELS,
  finished: 'Finished',
  feedDisconnected: 'Feed disconnected',
};

interface StatusPillProps {
  state: StatusPillState;
  /**
   * **Most screens want `sm`.** That is what the handoff renders everywhere —
   * contests table, catalogue rows, the modals, the Edit page headers — and
   * `lg` is the Contest Overview header alone.
   *
   * The default is `md`, the base `.m-pill` the design never renders unmodified,
   * because CAE-1537 published that geometry as the unqualified
   * `--status-pill-*` variables and a component whose bare form meant something
   * else would leave the two surfaces disagreeing about what "unqualified"
   * means. Settled that way deliberately; raised again in review and kept. Pass
   * the size you want rather than relying on the default.
   */
  size?: StatusPillSize;
  /**
   * Freezes the `live` dot's pulse (design-system.md §1: "Pause animation when
   * the contest is paused locally").
   *
   * This is **not** how a paused contest is normally drawn. The handoff swaps
   * the whole pill to `draft`'s slate and puts a separate "Paused" tag beside
   * it — pass `statusPillState(contest)` from `contests/lifecycle.ts`, which
   * does that swap, and the pill will be slate with no pulse to freeze. This
   * flag is for the surfaces that deliberately keep a contest green while it is
   * held, where a pulsing dot would claim entries are still arriving.
   */
  paused?: boolean;
  /**
   * Overrides the label for a caller whose vocabulary is not the contest
   * lifecycle's.
   *
   * `StatusPillState` spans fixture statuses as well as contest ones — that is
   * why `finished` is in the union — and the two disagree on one word:
   * `scheduled` is **Upcoming** for a contest and **Scheduled** for a fixture.
   * A fixture screen reaching for this pill should pass its own label rather
   * than inherit the contest one.
   */
  label?: string;
}

export const StatusPill = ({ state, size = 'md', paused = false, label }: StatusPillProps) => (
  <Box
    component="span"
    sx={(theme: Theme) => {
      const palette = theme.tokens.status[state];
      const { padding, gap, labelSize, labelWeight, labelTracking } =
        theme.tokens.statusPillSizes[size];

      return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${gap}px`,
        padding,
        borderRadius: `${theme.tokens.radius.pill}px`,
        border: `${theme.tokens.border.width} solid ${palette.border}`,
        backgroundColor: palette.bg,
        color: palette.text,
        fontFamily: theme.tokens.font.sans,
        fontSize: labelSize,
        fontWeight: labelWeight,
        letterSpacing: labelTracking,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      };
    }}
  >
    <Box
      aria-hidden
      component="span"
      sx={(theme: Theme) => ({
        width: `${theme.tokens.statusPillSizes[size].dotSize}px`,
        height: `${theme.tokens.statusPillSizes[size].dotSize}px`,
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: theme.tokens.status[state].dot,
        // Read through the variable, never the keyframe name — `admin-pulse` is
        // namespaced away from Tailwind's and stays an implementation detail.
        ...(state === 'live'
          ? {
              animation: 'var(--animation-pulse)',
              animationPlayState: paused ? 'paused' : 'running',
            }
          : {}),
      })}
    />
    {label ?? LABELS[state]}
  </Box>
);
